/**
 * CookieConsentEs - GDPR/CCPA compliant cookie consent banner
 * Spanish version
 *
 * @module components/CookieConsent
 * @description Spanish localization of the cookie consent banner
 */

import React, { useState, useEffect, useCallback } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { hasConsentBeenShown, setAnalyticsConsent } from "@/lib/analytics";

interface CookieConsentProps {
  /** Callback fired when user accepts cookies */
  onAccept?: () => void;
  /** Callback fired when user declines cookies */
  onDecline?: () => void;
  /** Delay before showing banner (ms) - default 1500 */
  showDelay?: number;
}

const CookieConsentEs: React.FC<CookieConsentProps> = ({
  onAccept,
  onDecline,
  showDelay = 1500,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Don't show if consent decision has already been made
    if (hasConsentBeenShown()) return;

    // Delay to not impact LCP
    const timer = setTimeout(() => setIsVisible(true), showDelay);
    return () => clearTimeout(timer);
  }, [showDelay]);

  const handleAccept = useCallback(() => {
    setAnalyticsConsent(true);
    setIsVisible(false);
    onAccept?.();
  }, [onAccept]);

  const handleDecline = useCallback(() => {
    setAnalyticsConsent(false);
    setIsVisible(false);
    onDecline?.();
  }, [onDecline]);

  return (
    <AnimatePresence>
      {isVisible && (
        <m.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 border-t bg-surface-raised border-border shadow-2"
          role="region"
          data-print="hide"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-description"
        >
          <div className="max-w-6xl px-4 py-3 mx-auto sm:px-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
              {/* Content */}
              <div className="flex-1">
                <h3
                  id="cookie-consent-title"
                  className="text-sm font-semibold text-primary"
                >
                  Su Privacidad Importa
                </h3>
                <p
                  id="cookie-consent-description"
                  className="text-sm leading-snug text-secondary"
                >
                  Este sitio utiliza cookies y tecnologías similares para
                  analizar el tráfico, entender cómo lo usa y mejorar su
                  experiencia. Sus datos me ayudan a servirle mejor.{" "}
                  <Link
                    to="/es/politica-de-privacidad"
                    className="underline text-accent-text"
                  >
                    Más información en nuestra Política de Privacidad
                  </Link>
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 md:flex-shrink-0">
                <button
                  type="button"
                  onClick={handleDecline}
                  className="flex-1 btn btn-secondary md:flex-none"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  className="flex-1 btn btn-secondary md:flex-none"
                >
                  Aceptar Todo
                </button>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentEs;
